import { Box, Stack, Typography } from '@mui/material';
import {
  Children,
  cloneElement,
  Fragment,
  isValidElement,
  PropsWithChildren,
  ReactNode,
} from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { Stars01Icon } from '@/components/icons/Stars01Icon';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { useIsCompactScreen } from '@/styles/hooks/useIsCompactScreen';
import { COLORS } from '@/styles/tokens/colors';

import { DiagramBar } from './DiagramBar';
import { KeyboardShortcutKey, ModifierKey } from './ModifierKey';

/**
 * Shared TileSelectionBar used by the diagram editors (estate waterfall,
 * ownership, single entity, and family tree). Provides the selection count,
 * multi-select hint, common group and notes actions, and a `children` slot for
 * diagram-specific action buttons, such as the icon shape picker.
 */

export function VerticalDivider() {
  return (
    <Box sx={{ width: '1px', height: 16, backgroundColor: COLORS.GRAY[200] }} />
  );
}

function isVerticalDivider(node: ReactNode): boolean {
  return isValidElement(node) && node.type === VerticalDivider;
}

/**
 * Flatten Fragments so leading-divider detection works when callers wrap each
 * action group in `<>...</>` (Children.toArray does not unwrap Fragments).
 */
function flattenChildren(children: ReactNode, parentPath = ''): ReactNode[] {
  return Children.toArray(children).flatMap((child, index) => {
    const path = `${parentPath}.${index}`;
    if (
      isValidElement<{ children?: ReactNode }>(child) &&
      child.type === Fragment
    ) {
      return flattenChildren(child.props.children, path);
    }
    return [isValidElement(child) ? cloneElement(child, { key: path }) : child];
  });
}

interface TileSelectionBarProps extends PropsWithChildren {
  selectedCount: number;
  groupEligibleCount?: number;
  onGroupTiles?: () => void;
  /**
   * When provided, a multi-tile selection always shows the "Group tiles"
   * button. If fewer than two tiles can be grouped, the button is disabled and
   * this text shows as its tooltip. When omitted, the button shows only when
   * two or more tiles can be grouped.
   */
  groupDisabledTooltip?: string;
  /** Override compact detection (defaults to screen-width breakpoint). */
  isCompact?: boolean;
  /**
   * When provided, renders a "Generate notes" action (AI tile notes) for the
   * current selection. Shown for any selection; the panel it opens handles
   * tiles that can't generate notes (warns and skips them).
   */
  onGenerateNotes?: () => void;
}

export function TileSelectionBar({
  selectedCount,
  groupEligibleCount = selectedCount,
  onGroupTiles,
  groupDisabledTooltip,
  isCompact: isCompactOverride,
  onGenerateNotes,
  children,
}: TileSelectionBarProps) {
  const isCompactScreen = useIsCompactScreen();
  const isCompact = isCompactOverride ?? isCompactScreen;

  const helperText = (
    <Typography variant="body-xs">
      <ModifierKey hideIcon /> + click to select more
    </Typography>
  );

  if (selectedCount === 0) {
    return (
      <DiagramBar paddingX="12px" borderRadius="6px">
        <Typography variant="body-xs">
          <ModifierKey hideIcon /> + click or{' '}
          <KeyboardShortcutKey>Shift</KeyboardShortcutKey> + drag to select
          multiple tiles
        </Typography>
      </DiagramBar>
    );
  }

  // Once tiles are selected we drop the "N selected / click to select more"
  // copy and show only action buttons, matching the estate waterfall. Consumers
  // prefix each action group with a leading <VerticalDivider /> to separate it
  // from the (now-removed) label; without a preceding Generate-notes button
  // that first divider would dangle at the bar's start, so strip it.
  const items = flattenChildren(children);
  const canGroup = groupEligibleCount > 1;
  const showDisabledGroup =
    !canGroup && !!onGroupTiles && !!groupDisabledTooltip && selectedCount > 1;
  const hasGroupAction = (canGroup && !!onGroupTiles) || showDisabledGroup;
  const actions =
    !onGenerateNotes && !hasGroupAction && isVerticalDivider(items[0])
      ? items.slice(1)
      : items;

  // No action buttons for this selection (e.g. a family-tree selection with no
  // alignable tiles) — keep the count + hint so the bar still reads as active.
  if (!onGenerateNotes && !hasGroupAction && actions.length === 0) {
    const selectedLabel =
      selectedCount === 1 ? '1 selected' : `${selectedCount} selected`;
    return (
      <DiagramBar paddingX="12px">
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="title-xs">{selectedLabel}</Typography>
          {!isCompact && helperText}
        </Stack>
      </DiagramBar>
    );
  }

  return (
    <DiagramBar>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {canGroup && onGroupTiles && (
          <Button variant="transparent" size="xs" onClick={onGroupTiles}>
            Group tiles
            {groupEligibleCount < selectedCount
              ? ` (${groupEligibleCount})`
              : ''}
          </Button>
        )}
        {showDisabledGroup && (
          <Tooltip title={groupDisabledTooltip}>
            <Button variant="transparent" size="xs" disabled>
              Group tiles
            </Button>
          </Tooltip>
        )}
        {onGenerateNotes && (
          <Button
            variant="transparent"
            size="xs"
            startIcon={Stars01Icon}
            iconProps={{ color: COLORS.TEAL[600], size: 16 }}
            onClick={onGenerateNotes}
          >
            Generate notes
          </Button>
        )}
        {actions}
      </Stack>
    </DiagramBar>
  );
}
